// === Admin: Chemicals / Fertilizers URL Check (interactive) ===
// Pre-loads every chemical + fertilizer that has at least one external URL,
// then lets the admin tick which to probe and run them sequentially with
// live UI updates. On a failed probe the backend asks Claude for a
// verified replacement; the admin can Accept (writes to DB) or Reject
// (clears local state).
//
// Probe = read-only (no DB writes). Apply = commits the chosen URL.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  OpenInNew as OpenInNewIcon,
  ScheduleSend as ScheduleSendIcon,
  Link as LinkIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAdminApi } from '../lib/use-admin-api';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
} from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Source = 'chemical' | 'fertilizer';
type LinkField = 'sds' | 'label';
type ProbeStatus = 'idle' | 'running' | 'active' | 'issue' | 'applied';
type ViewMode = 'chemical' | 'fertilizer';

const PAGE_SIZE = 10;

interface ProbeState {
  status: ProbeStatus;
  message: string | null;
  replacement: string | null;
  appliedUrl: string | null;
}

interface CheckRow {
  source: Source;
  id: number;
  product: string;
  company: string | null;
  sdsUrl: string | null;        // null = bucket file or empty (don't probe)
  labelUrl: string | null;      // chemicals only; null otherwise
  sds: ProbeState;
  label: ProbeState;            // unused for fertilizers
}

const IDLE_STATE: ProbeState = { status: 'idle', message: null, replacement: null, appliedUrl: null };

// Response from GET /document-link-check/count-urls.
interface UrlCountResult {
  chemicals: { rows: number; sdsExternal: number; labelExternal: number; sdsBucket: number; labelBucket: number };
  fertilizers: { rows: number; sdsExternal: number; sdsBucket: number };
  totalExternal: number;
  totalBucket: number;
}

// Results from POST /document-link-check/run (discriminated by `mode`).
interface ProbeRunResult {
  mode: 'probe';
  durationMs: number;
  rowsProbed: number;
  linksProbed: number;
  activeCount: number;
  issueCount: number;
  capReached: boolean;
}
interface DigestRunResult {
  mode: 'digest';
  durationMs: number;
  issueCount: number;
  emailSent: boolean;
  emailError: string | null;
  recipients: string[];
}
type RunResult = ProbeRunResult | DigestRunResult;

// ---------------------------------------------------------------------------
// API response types from existing list endpoints
// ---------------------------------------------------------------------------

interface ChemicalApiRow {
  id: number;
  product: string | null;
  fertilzer_company: number | string | null;
  safety_data_sheet_link: string | null;
  chemical_label: string | null;
  // Persisted state from prior runs (cron or manual)
  sds_link_status: 'active' | 'issue' | null;
  sds_link_status_message: string | null;
  sds_link_replacement_url: string | null;
  sds_link_last_checked_at: string | null;
  label_link_status: 'active' | 'issue' | null;
  label_link_status_message: string | null;
  label_link_replacement_url: string | null;
  label_link_last_checked_at: string | null;
}

interface ChemicalsListResponse {
  total: number;
  data: ChemicalApiRow[];
}

interface ChemicalsLookupResponse {
  fertilzerCompanies: { id: number; company: string | null }[];
}

interface FertilizerApiRow {
  id: number;
  fertilzer: string | null;
  fertilzer_company: number | string | null;
  safety_data_sheet_link: string | null;
  fertilzer_companies?: { id: number; company: string } | null;
  sds_link_status: 'active' | 'issue' | null;
  sds_link_status_message: string | null;
  sds_link_replacement_url: string | null;
  sds_link_last_checked_at: string | null;
}

interface FertilizersListResponse {
  total: number;
  data: FertilizerApiRow[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isExternal(v: string | null | undefined): boolean {
  return Boolean(v) && (v!.startsWith('http://') || v!.startsWith('https://'));
}

function rowKey(r: CheckRow): string {
  return `${r.source}-${r.id}`;
}

// A row is "broken" if either of its links last probed as an issue.
function rowHasIssue(r: CheckRow): boolean {
  return r.sds.status === 'issue' || r.label.status === 'issue';
}

function stateFromDb(
  status: 'active' | 'issue' | null,
  message: string | null,
  replacement: string | null,
): ProbeState {
  if (status === null) return { ...IDLE_STATE };
  return { status, message, replacement, appliedUrl: null };
}

function lookupCompany(map: Map<number, string>, raw: number | string | null | undefined): string | null {
  if (raw == null) return null;
  // The chemical table stores fertilzer_company as integer FK, but PostgREST
  // can return it as either number or numeric string depending on column
  // type discovery — coerce both.
  const id = typeof raw === 'number' ? raw : (/^\d+$/.test(raw) ? Number(raw) : null);
  if (id == null) {
    // Not a numeric value — assume it's already the company name.
    return typeof raw === 'string' ? raw : null;
  }
  return map.get(id) ?? null;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DocumentLinkCheckPage() {
  const navigate = useNavigate();
  const api = useAdminApi();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [chemicalRows, setChemicalRows] = useState<readonly CheckRow[]>([]);
  const [fertilizerRows, setFertilizerRows] = useState<readonly CheckRow[]>([]);

  const [view, setView] = useState<ViewMode>('chemical');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [running, setRunning] = useState(false);
  const cancelRef = useRef(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // ---- "Test Cron job" — server-side full run (probes everything, writes DB,
  // emails the digest to the configured recipients), then shows a summary. ----
  const [testRunning, setTestRunning] = useState(false);
  const [runResult, setRunResult] = useState<RunResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [showTestDialog, setShowTestDialog] = useState(false);
  // Which action the confirm dialog is for (null = closed).
  const [confirmAction, setConfirmAction] = useState<'probe' | 'digest' | null>(null);

  // ---- "Count URLs" — count external document links in the DB ----
  const [countLoading, setCountLoading] = useState(false);
  const [countResult, setCountResult] = useState<UrlCountResult | null>(null);
  const [countError, setCountError] = useState<string | null>(null);
  const [showCountDialog, setShowCountDialog] = useState(false);

  // Reset to page 1 whenever filter or tab changes
  useEffect(() => { setPage(1); }, [view, search]);

  // ---- Initial load ----
  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [chemRes, fertRes, lookupRes] = await Promise.all([
        api.get<ChemicalsListResponse>('/api/v1/admin/chemicals?limit=500'),
        api.get<FertilizersListResponse>('/api/v1/admin/fertilizers?limit=500'),
        api.get<ChemicalsLookupResponse>('/api/v1/admin/chemicals/lookup-data'),
      ]);
      if (!chemRes.success) throw chemRes.error;
      if (!fertRes.success) throw fertRes.error;

      const companyMap = new Map<number, string>();
      if (lookupRes.success) {
        for (const c of lookupRes.data.fertilzerCompanies ?? []) {
          if (c.company) companyMap.set(c.id, c.company);
        }
      }

      const chemRows: CheckRow[] = [];
      for (const r of chemRes.data.data ?? []) {
        const sds = isExternal(r.safety_data_sheet_link) ? r.safety_data_sheet_link : null;
        const label = isExternal(r.chemical_label) ? r.chemical_label : null;
        if (!sds && !label) continue;
        chemRows.push({
          source: 'chemical',
          id: r.id,
          product: r.product ?? `chemical #${r.id}`,
          company: lookupCompany(companyMap, r.fertilzer_company),
          sdsUrl: sds,
          labelUrl: label,
          sds: sds ? stateFromDb(r.sds_link_status, r.sds_link_status_message, r.sds_link_replacement_url) : { ...IDLE_STATE },
          label: label ? stateFromDb(r.label_link_status, r.label_link_status_message, r.label_link_replacement_url) : { ...IDLE_STATE },
        });
      }

      const fertRows: CheckRow[] = [];
      for (const r of fertRes.data.data ?? []) {
        const sds = isExternal(r.safety_data_sheet_link) ? r.safety_data_sheet_link : null;
        if (!sds) continue;
        fertRows.push({
          source: 'fertilizer',
          id: r.id,
          product: r.fertilzer ?? `fertilizer #${r.id}`,
          company: r.fertilzer_companies?.company ?? lookupCompany(companyMap, r.fertilzer_company),
          sdsUrl: sds,
          labelUrl: null,
          sds: stateFromDb(r.sds_link_status, r.sds_link_status_message, r.sds_link_replacement_url),
          label: { ...IDLE_STATE },
        });
      }

      setChemicalRows(chemRows);
      setFertilizerRows(fertRows);

      // Start with nothing selected — user opts in by ticking rows.
      setSelected(new Set());
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => () => { cancelRef.current = true; }, []);

  // ---- Test Cron job: probe one batch of links now ----
  const executeProbe = useCallback(async () => {
    setConfirmAction(null);
    setTestRunning(true);
    setTestError(null);
    setRunResult(null);
    setShowTestDialog(true);
    try {
      const res = await api.post<ProbeRunResult>(
        '/api/v1/admin/document-link-check/run',
        { mode: 'probe' },
      );
      if (!res.success) setTestError(res.error.message);
      else {
        setRunResult(res.data);
        void reload(); // refresh table so freshly-probed statuses appear
      }
    } catch (e) {
      setTestError(e instanceof Error ? e.message : 'Run failed');
    } finally {
      setTestRunning(false);
    }
  }, [api, reload]);

  // ---- Send Email Digest: email all currently-broken links now ----
  const executeDigest = useCallback(async () => {
    setConfirmAction(null);
    setTestRunning(true);
    setTestError(null);
    setRunResult(null);
    setShowTestDialog(true);
    try {
      const res = await api.post<DigestRunResult>(
        '/api/v1/admin/document-link-check/run',
        { mode: 'digest', sendEmail: true },
      );
      if (!res.success) setTestError(res.error.message);
      else setRunResult(res.data);
    } catch (e) {
      setTestError(e instanceof Error ? e.message : 'Digest failed');
    } finally {
      setTestRunning(false);
    }
  }, [api]);

  // ---- Count URLs: how many external links live in the DB ----
  const countUrls = useCallback(async () => {
    setCountLoading(true);
    setCountError(null);
    setCountResult(null);
    setShowCountDialog(true);
    try {
      const res = await api.get<UrlCountResult>('/api/v1/admin/document-link-check/count-urls');
      if (!res.success) setCountError(res.error.message);
      else setCountResult(res.data);
    } catch (e) {
      setCountError(e instanceof Error ? e.message : 'Count failed');
    } finally {
      setCountLoading(false);
    }
  }, [api]);

  // ---- Active rows for the current tab ----
  const sourceRows = view === 'chemical' ? chemicalRows : fertilizerRows;

  // Issue counts per tab (for labels + summary), independent of search/page.
  const chemIssueCount = useMemo(() => chemicalRows.filter(rowHasIssue).length, [chemicalRows]);
  const fertIssueCount = useMemo(() => fertilizerRows.filter(rowHasIssue).length, [fertilizerRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sourceRows.filter((r) => {
      // Triage view: only broken links.
      if (!rowHasIssue(r)) return false;
      if (!q) return true;
      return r.product.toLowerCase().includes(q) || (r.company ?? '').toLowerCase().includes(q);
    });
  }, [sourceRows, search]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, page]);

  // ---- Selection helpers ----
  const toggleRow = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const pagedKeys = pagedRows.map(rowKey);
  const allPagedSelected = pagedKeys.length > 0 && pagedKeys.every((k) => selected.has(k));
  const somePagedSelected = pagedKeys.some((k) => selected.has(k));
  const togglePagedSelection = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPagedSelected) {
        for (const k of pagedKeys) next.delete(k);
      } else {
        for (const k of pagedKeys) next.add(k);
      }
      return next;
    });
  };

  // ---- Update one row's probe state immutably ----
  const updateRow = useCallback((source: Source, id: number, field: LinkField, patch: Partial<ProbeState>) => {
    const setter = source === 'chemical' ? setChemicalRows : setFertilizerRows;
    setter((rows) => rows.map((r) =>
      r.id === id && r.source === source
        ? { ...r, [field]: { ...r[field], ...patch } }
        : r,
    ));
  }, []);

  // ---- Probe a single (row, field) ----
  const probeOne = useCallback(async (row: CheckRow, field: LinkField) => {
    const url = field === 'sds' ? row.sdsUrl : row.labelUrl;
    if (!url) return;
    updateRow(row.source, row.id, field, { status: 'running', message: null, replacement: null, appliedUrl: null });
    const res = await api.post<{ status: 'active' | 'issue'; message: string | null; replacement: string | null }>(
      '/api/v1/admin/document-link-check/probe',
      {
        url,
        productName: row.product,
        companyName: row.company,
        type: field,
        findReplacement: true,
        // source + id make the backend persist the result to the same DB
        // columns the nightly cron writes to, so the page reflects prior
        // runs after a refresh.
        source: row.source,
        id: row.id,
      },
    );
    if (!res.success) {
      updateRow(row.source, row.id, field, { status: 'issue', message: res.error.message, replacement: null });
      return;
    }
    updateRow(row.source, row.id, field, {
      status: res.data.status,
      message: res.data.message,
      replacement: res.data.replacement,
      appliedUrl: null,
    });
  }, [api, updateRow]);

  // ---- Run-selected (across BOTH tabs — selected set spans them) ----
  const runSelected = useCallback(async () => {
    cancelRef.current = false;

    interface Job { row: CheckRow; field: LinkField; }
    const jobs: Job[] = [];
    for (const row of [...chemicalRows, ...fertilizerRows]) {
      if (!selected.has(rowKey(row))) continue;
      if (row.sdsUrl) jobs.push({ row, field: 'sds' });
      if (row.labelUrl) jobs.push({ row, field: 'label' });
    }
    if (jobs.length === 0) return;

    setRunning(true);
    setProgress({ done: 0, total: jobs.length });
    try {
      for (let i = 0; i < jobs.length; i++) {
        if (cancelRef.current) break;
        const job = jobs[i]!;
        await probeOne(job.row, job.field);
        setProgress({ done: i + 1, total: jobs.length });
        await new Promise((r) => setTimeout(r, 150));
      }
    } finally {
      setRunning(false);
    }
  }, [chemicalRows, fertilizerRows, selected, probeOne]);

  // ---- Probe a single row (re-run icon) ----
  const probeRow = useCallback(async (row: CheckRow) => {
    if (running) return;
    setRunning(true);
    try {
      if (row.sdsUrl) await probeOne(row, 'sds');
      if (row.labelUrl) await probeOne(row, 'label');
    } finally {
      setRunning(false);
    }
  }, [running, probeOne]);

  // ---- Apply (commit replacement) ----
  const applyReplacement = useCallback(async (row: CheckRow, field: LinkField) => {
    const state = row[field];
    if (!state.replacement) return;
    updateRow(row.source, row.id, field, { status: 'running' });
    const res = await api.post('/api/v1/admin/document-link-check/apply', {
      source: row.source,
      id:     row.id,
      field,
      url:    state.replacement,
    });
    if (!res.success) {
      updateRow(row.source, row.id, field, {
        status: 'issue',
        message: `Apply failed: ${res.error.message}`,
      });
      return;
    }
    updateRow(row.source, row.id, field, {
      status: 'applied',
      message: null,
      replacement: null,
      appliedUrl: state.replacement,
    });
  }, [api, updateRow]);

  const rejectReplacement = useCallback(async (row: CheckRow, field: LinkField) => {
    // Optimistic local update; then clear the DB candidate so it doesn't
    // reappear on next page load.
    updateRow(row.source, row.id, field, { replacement: null });
    const path = row.source === 'chemical' ? `/api/v1/admin/chemicals/${row.id}/reject-replacement` : `/api/v1/admin/fertilizers/${row.id}/reject-replacement`;
    const res = await api.post(path, { field });
    if (!res.success) {
      // Rollback on failure
      updateRow(row.source, row.id, field, { replacement: row[field].replacement });
    }
  }, [api, updateRow]);

  // ---- Aggregate counts (across BOTH tabs) ----
  const counts = useMemo(() => {
    let probed = 0, active = 0, issues = 0;
    for (const r of [...chemicalRows, ...fertilizerRows]) {
      for (const field of ['sds', 'label'] as const) {
        const url = field === 'sds' ? r.sdsUrl : r.labelUrl;
        if (!url) continue;
        const s = r[field].status;
        if (s !== 'idle') probed++;
        if (s === 'active' || s === 'applied') active++;
        if (s === 'issue') issues++;
      }
    }
    return { probed, active, issues };
  }, [chemicalRows, fertilizerRows]);

  const includeLabel = view === 'chemical';

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Tooltip title="Back to Dashboard">
          <IconButton onClick={() => navigate('/dashboard')} size="small">
            <BackIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Chemicals / Fertilizers URL Check</Typography>
          <Typography variant="body2" color="text.secondary">
            Shows only broken links needing a fix. Run the cron to re-check; accept an Australian (.au) replacement to resolve.
          </Typography>
        </Box>
        <Tooltip title="Reload from database">
          <span>
            <IconButton onClick={() => void reload()} disabled={loading || running}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Tab switch */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, v) => { if (v) setView(v); }}
          size="small"
        >
          <ToggleButton value="chemical">
            Chemicals ({chemIssueCount} broken)
          </ToggleButton>
          <ToggleButton value="fertilizer">
            Fertilizers ({fertIssueCount} broken)
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search product or company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            sx={{ width: 320 }}
          />
          {search && (
            <Button size="small" onClick={() => setSearch('')}>Clear search</Button>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              // Select the rows currently visible (respects the broken-only filter + search).
              const all = new Set(selected);
              for (const r of filteredRows) all.add(rowKey(r));
              setSelected(all);
            }}
            disabled={running || filteredRows.length === 0}
          >
            Select all
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setSelected(new Set())}
            disabled={running || selected.size === 0}
          >
            Clear
          </Button>
          <Tooltip title="Probe the next batch of links now (the scheduled cron covers everything every few hours)">
            <span>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={testRunning ? <CircularProgress size={16} color="inherit" /> : <ScheduleSendIcon />}
                onClick={() => setConfirmAction('probe')}
                disabled={running || testRunning || loading}
              >
                {testRunning ? 'Working…' : 'Test Cron job'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Email the digest of all currently-broken links to the configured recipients now">
            <span>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<EmailIcon />}
                onClick={() => setConfirmAction('digest')}
                disabled={running || testRunning || loading}
              >
                Send Email Digest
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Count external (http/https) document URLs in the database — excludes bucket-hosted files">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={countLoading ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                onClick={() => void countUrls()}
                disabled={countLoading}
              >
                {countLoading ? 'Counting…' : 'Count URLs'}
              </Button>
            </span>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {selected.size} selected · probed {counts.probed} · {counts.active} active · {counts.issues} issues
          </Typography>
          <Button
            variant="contained"
            startIcon={running ? <CircularProgress size={18} color="inherit" /> : <PlayIcon />}
            onClick={() => void runSelected()}
            disabled={running || selected.size === 0 || loading}
          >
            {running ? `Running ${progress.done}/${progress.total}…` : 'Run selected'}
          </Button>
          {running && (
            <Button size="small" color="inherit" onClick={() => { cancelRef.current = true; }}>
              Stop
            </Button>
          )}
        </Box>
      </Paper>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {/* Table header banner */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {view === 'chemical' ? 'Chemicals' : 'Fertilizers'} —
              {' '}{filteredRows.length}{' '}broken link{filteredRows.length === 1 ? '' : 's'}
            </Typography>
          </Box>

          <TableContainer>
            <Table size="small">
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell sx={{ width: 48 }}>
                    <Checkbox
                      size="small"
                      checked={allPagedSelected}
                      indeterminate={!allPagedSelected && somePagedSelected}
                      onChange={togglePagedSelection}
                      disabled={running || pagedRows.length === 0}
                    />
                  </AppTableHeadCell>
                  <AppTableHeadCell sx={{ minWidth: 200 }}>Product</AppTableHeadCell>
                  <AppTableHeadCell sx={{ minWidth: 160 }}>Company</AppTableHeadCell>
                  <AppTableHeadCell>SDS link / status</AppTableHeadCell>
                  {includeLabel && <AppTableHeadCell>Label link / status</AppTableHeadCell>}
                  <AppTableHeadCell sx={{ width: 60 }} align="center">Re-run</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {pagedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={includeLabel ? 6 : 5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {search
                        ? 'No matches.'
                        : 'No broken links 🎉 — run the cron to re-check.'}
                    </TableCell>
                  </TableRow>
                ) : pagedRows.map((row) => {
                  const key = rowKey(row);
                  return (
                    <AppTableRow key={key}>
                      <TableCell>
                        <Checkbox
                          size="small"
                          checked={selected.has(key)}
                          onChange={() => toggleRow(key)}
                          disabled={running}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{row.product}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{row.company ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <LinkStatusCell
                          url={row.sdsUrl}
                          state={row.sds}
                          onAccept={() => applyReplacement(row, 'sds')}
                          onReject={() => rejectReplacement(row, 'sds')}
                          disabled={running}
                        />
                      </TableCell>
                      {includeLabel && (
                        <TableCell>
                          <LinkStatusCell
                            url={row.labelUrl}
                            state={row.label}
                            onAccept={() => applyReplacement(row, 'label')}
                            onReject={() => rejectReplacement(row, 'label')}
                            disabled={running}
                          />
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Tooltip title="Re-run this row">
                          <span>
                            <IconButton size="small" onClick={() => probeRow(row)} disabled={running}>
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </AppTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {filteredRows.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
              </Typography>
              {pageCount > 1 && (
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Count URLs — results dialog */}
      <Dialog
        open={showCountDialog}
        onClose={() => { if (!countLoading) setShowCountDialog(false); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LinkIcon color="primary" />
          External URLs in the database
        </DialogTitle>
        <DialogContent dividers>
          {countLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">Counting…</Typography>
            </Box>
          )}
          {!countLoading && countError && <Alert severity="error">{countError}</Alert>}
          {!countLoading && countResult && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="h3" fontWeight={800} color="primary.main">
                  {countResult.totalExternal}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  external links the cron checks
                </Typography>
              </Box>
              <Divider />
              <SummaryRow label="Chemicals — SDS links" value={String(countResult.chemicals.sdsExternal)} />
              <SummaryRow label="Chemicals — label links" value={String(countResult.chemicals.labelExternal)} />
              <SummaryRow label="Fertilizers — SDS links" value={String(countResult.fertilizers.sdsExternal)} />
              <Divider />
              <SummaryRow
                label="Stored files (hosted by us, not checked)"
                value={String(countResult.totalBucket)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCountDialog(false)} disabled={countLoading}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog (probe batch OR send digest) */}
      <Dialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        maxWidth="xs"
        fullWidth
      >
        {confirmAction === 'digest' ? (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <EmailIcon color="secondary" />
              Send the email digest now?
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Emails <strong>all currently-broken links</strong> (from the last checks) as one digest to the configured recipients. Does not probe anything — it's instant.
              </Typography>
              <Alert severity="warning" variant="outlined">This sends a real email.</Alert>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setConfirmAction(null)} color="inherit">Cancel</Button>
              <Button onClick={() => void executeDigest()} variant="contained" color="secondary" startIcon={<EmailIcon />}>
                Send now
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ScheduleSendIcon color="secondary" />
              Probe a batch now?
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Checks the next batch of least-recently-checked links and saves their status. One Cloudflare run can't check all ~700 at once, so the scheduled cron cycles through everything every few hours — this just runs one batch on demand.
              </Typography>
              <Alert severity="info" variant="outlined">Takes a few seconds.</Alert>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setConfirmAction(null)} color="inherit">Cancel</Button>
              <Button onClick={() => void executeProbe()} variant="contained" color="secondary" startIcon={<ScheduleSendIcon />}>
                Run batch
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Results dialog (probe batch OR digest) */}
      <Dialog
        open={showTestDialog}
        onClose={() => { if (!testRunning) setShowTestDialog(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{runResult?.mode === 'digest' ? 'Email digest — results' : 'Probe batch — results'}</DialogTitle>
        <DialogContent dividers>
          {testRunning && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">Working…</Typography>
            </Box>
          )}

          {!testRunning && testError && <Alert severity="error">{testError}</Alert>}

          {!testRunning && runResult?.mode === 'probe' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <SummaryRow label="Rows probed this batch" value={String(runResult.rowsProbed)} />
              <SummaryRow label="Links probed" value={String(runResult.linksProbed)} />
              <SummaryRow label="Active (OK)" value={String(runResult.activeCount)} />
              <SummaryRow
                label="Broken in this batch"
                value={String(runResult.issueCount)}
                emphasis={runResult.issueCount > 0 ? 'error' : 'success'}
              />
              <Divider />
              {runResult.capReached ? (
                <Alert severity="info">
                  Hit Cloudflare's per-run limit — the rest are covered by the next scheduled runs (every 10 min). Re-run to probe the next batch now.
                </Alert>
              ) : (
                <Alert severity="success">Batch complete. The scheduled cron keeps the rest up to date.</Alert>
              )}
              <Typography variant="caption" color="text.secondary">
                Completed in {(runResult.durationMs / 1000).toFixed(1)}s.
              </Typography>
            </Box>
          )}

          {!testRunning && runResult?.mode === 'digest' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <SummaryRow
                label="Broken links in digest"
                value={String(runResult.issueCount)}
                emphasis={runResult.issueCount > 0 ? 'error' : 'success'}
              />
              <Divider />
              {runResult.issueCount === 0 ? (
                <Alert severity="success">No broken links on record — nothing to email.</Alert>
              ) : runResult.emailSent ? (
                <Alert severity="success">Digest emailed to: {(runResult.recipients ?? []).join(', ')}</Alert>
              ) : (
                <Alert severity="error">
                  Email NOT sent{runResult.emailError ? ` — ${runResult.emailError}` : ''}.
                  {(runResult.recipients ?? []).length > 0 && <> (recipients: {(runResult.recipients ?? []).join(', ')})</>}
                </Alert>
              )}
              <Typography variant="caption" color="text.secondary">
                Completed in {(runResult.durationMs / 1000).toFixed(1)}s.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTestDialog(false)} disabled={testRunning}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

// Small label/value row used in the Test Cron results dialog.
function SummaryRow({ label, value, emphasis }: { label: string; value: string; emphasis?: 'error' | 'success' }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography
        variant="body2"
        fontWeight={700}
        color={emphasis === 'error' ? 'error.main' : emphasis === 'success' ? 'success.main' : 'text.primary'}
      >
        {value}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// LinkStatusCell — URL on top, status icon + reason / replacement below
// ---------------------------------------------------------------------------

interface LinkStatusCellProps {
  readonly url: string | null;
  readonly state: ProbeState;
  readonly onAccept: () => void;
  readonly onReject: () => void;
  readonly disabled: boolean;
}

function LinkStatusCell({ url, state, onAccept, onReject, disabled }: LinkStatusCellProps) {
  if (!url) {
    return <Typography variant="caption" color="text.disabled">(bucket file or empty)</Typography>;
  }

  const liveUrl = state.appliedUrl ?? url;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: 12 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <a
          href={liveUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ wordBreak: 'break-all', flex: 1, color: state.status === 'issue' ? '#c62828' : 'inherit' }}
        >
          {liveUrl}
        </a>
        <IconButton size="small" onClick={() => window.open(liveUrl, '_blank', 'noopener,noreferrer')}>
          <OpenInNewIcon fontSize="inherit" />
        </IconButton>
      </Box>

      {state.status === 'idle' && (
        <Typography variant="caption" color="text.disabled">— not yet probed —</Typography>
      )}
      {state.status === 'running' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Probing…</Typography>
        </Box>
      )}
      {state.status === 'active' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CheckIcon fontSize="small" color="success" />
          <Typography variant="caption" color="success.main" fontWeight={600}>Active</Typography>
        </Box>
      )}
      {state.status === 'applied' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CheckIcon fontSize="small" color="success" />
          <Typography variant="caption" color="success.main" fontWeight={600}>
            Applied (was broken)
          </Typography>
          <Chip size="small" variant="outlined" label="saved to DB" sx={{ fontSize: 10, height: 18 }} />
        </Box>
      )}
      {state.status === 'issue' && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CancelIcon fontSize="small" color="error" />
            <Typography variant="caption" color="error.main" fontWeight={600}>
              {state.message ?? 'Issue'}
            </Typography>
          </Box>
          {state.replacement ? (
            <Box sx={{ pl: 0.5, mt: 0.5, borderLeft: '2px solid', borderColor: 'success.light' }}>
              <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                  Suggested replacement:
                </Typography>
                <a
                  href={state.replacement}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ wordBreak: 'break-all', color: '#2e7d32', fontSize: 11 }}
                >
                  {state.replacement}
                </a>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={onAccept}
                    disabled={disabled}
                    sx={{ minWidth: 0, py: 0, px: 1, fontSize: 11 }}
                  >
                    Accept
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={onReject}
                    disabled={disabled}
                    sx={{ minWidth: 0, py: 0, px: 1, fontSize: 11 }}
                  >
                    Reject
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography variant="caption" sx={{ pl: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
              No replacement found by AI search.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
